/**
 * §P.8 · El registro de accesos a los datos de este club.
 *
 * ## Existe para que el club nos audite a nosotros
 *
 * `api_first.md` §A dice, literalmente, «todo acceso a datos de un club queda en
 * `audit_log`, **y el club puede verlo**». Durante un tiempo no hubo endpoint por el
 * que verlo, y un registro que solo puede leer quien audita no es transparencia: es
 * una promesa de folleto.
 *
 * Es también lo que hace defendible tener un backoffice que lo ve todo. Podemos
 * decirle a un cliente que miramos su cuenta, cuándo y por qué, y que lo compruebe
 * él mismo. Con datos de menores, esa es la diferencia entre soporte y vigilancia.
 *
 * Por eso **las entradas nuestras se marcan** en vez de mezclarse: una entrada de
 * plataforma indistinguible de las del club cumple la letra de la promesa y no la
 * promesa. Y hay un filtro para ver solo esas, que es lo que de verdad va a buscar
 * quien abre esta pantalla con una duda.
 *
 * ## Los filtros van en el servidor y no en el navegador
 *
 * Un registro de auditoría crece sin techo y el endpoint tope a 500 filas. Filtrar
 * en el cliente sobre esas 500 enseñaría «no hay accesos de Kiap en junio» cuando lo
 * que pasa es que junio no cabía en la página. Aquí eso es dar una respuesta falsa a
 * la única pregunta que importa.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fechaYHora } from '@/shared/ui/formato';
import {
  agruparLecturasDeSoporte,
  esAccesoDeKiap,
  etiquetaDeAccion,
  rutaDeLectura,
  type EntradaDeAuditoria,
} from '@/club/cumplimiento/tipos';

/*
 * El tope del servidor. Era 200, y desde que cada pantalla mirada en soporte deja su
 * fila (05-11-2026) una entrada larga podía ocupar casi todo.
 */
const TOPE = 500;

export function RegistroDeAccesos({ club }: { club: string }) {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const registro = useQuery({
    queryKey: ['auditoria', club, desde, hasta],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club })['audit-log'].get({
        query: {
          limit: TOPE,
          ...(desde === '' ? {} : { from: desde }),
          ...(hasta === '' ? {} : { to: hasta }),
        },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as EntradaDeAuditoria[];
    },
  });

  const entradas = registro.data ?? [];
  const deKiap = entradas.filter(esAccesoDeKiap).length;

  return (
    <section>
      <h1>Registro de accesos</h1>

      <p className="tenue">
        Todo acceso a los datos de este club queda aquí,{' '}
        <strong>incluidos los nuestros</strong>. Es lo que permite comprobar por
        tu cuenta cuándo hemos mirado tu cuenta y por qué, sin tener que fiarte
        de que te lo contemos.
      </p>

      <div className="filtros">
        <label>
          Desde
          <input
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
        </label>
        <label>
          Hasta
          <input
            type="date"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
        </label>
        {(desde !== '' || hasta !== '') && (
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setDesde('');
              setHasta('');
            }}
          >
            Quitar las fechas
          </button>
        )}
      </div>

      {registro.isPending && (
        <Cargando filas={6} etiqueta="Cargando el registro" />
      )}

      {registro.isError && (
        <PanelError
          problema={aProblema(registro.error)}
          reintentar={() => void registro.refetch()}
        />
      )}

      {registro.data && entradas.length === 0 && (
        <Vacio
          titulo="No hay accesos registrados"
          explicacion={
            desde === '' && hasta === ''
              ? 'Todavía no se ha descargado ni consultado nada que deje rastro. Aquí aparecerán las descargas de ficheros, las fichas médicas que se miren y cualquier entrada nuestra en modo soporte.'
              : 'En esas fechas no hay ningún acceso. Prueba a ensanchar el rango.'
          }
        />
      )}

      {entradas.length > 0 && (
        <>
          {/*
            Lo primero que se dice es cuántas son nuestras, porque es la pregunta con
            la que se abre esta pantalla. Y «ninguna» se dice en voz alta: dejar que
            se deduzca de no ver ninguna etiqueta es hacer contar filas a mano.
          */}
          <div className="aviso aviso-calma" role="status">
            {deKiap === 0 ? (
              <>
                <strong>Ningún acceso de Kiap</strong>{' '}
                {entradas.length === 1
                  ? 'en la única entrada que hay'
                  : `entre las ${entradas.length} entradas`}
                .
              </>
            ) : (
              <>
                <strong>
                  {deKiap === 1
                    ? 'Un acceso de Kiap'
                    : `${deKiap} accesos de Kiap`}
                </strong>
                {entradas.length === 1
                  ? ', y es la única entrada'
                  : ` entre las ${entradas.length} entradas`}
                . Salen marcados, y el motivo de cada entrada en soporte está en
                la pestaña «Modo soporte».
              </>
            )}
          </div>

          <ul className="auditoria">
            {agruparLecturasDeSoporte(entradas).map((fila) => {
              if (fila.tipo === 'lecturas') {
                const reciente = fila.entradas[0]!;
                const antigua = fila.entradas.at(-1)!;
                return (
                  <li key={reciente.id} className="fila-marcada">
                    <div className="aviso-cabeza">
                      <strong>
                        Miró en modo soporte · {fila.entradas.length} pantallas
                      </strong>
                      <span className="pastilla pastilla-info">
                        Personal de Kiap
                      </span>
                    </div>
                    <p className="tenue">
                      {reciente.actor_name ?? 'Sin identificar'}
                      {reciente.ip ? ` · desde ${reciente.ip}` : ''} · de{' '}
                      {fechaYHora(antigua.occurred_at)} a{' '}
                      {fechaYHora(reciente.occurred_at)}
                    </p>
                    {/* Juntas para leerse, y todas a un clic: no se esconde ninguna. */}
                    <details>
                      <summary>Ver las {fila.entradas.length}</summary>
                      <ul className="lecturas-de-soporte">
                        {fila.entradas.map((l) => (
                          <li key={l.id}>
                            {fechaYHora(l.occurred_at)} ·{' '}
                            <code>{rutaDeLectura(l)}</code>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </li>
                );
              }

              const e = fila.entrada;
              const nuestro = esAccesoDeKiap(e);
              const datos = Object.entries(e.metadata ?? {});

              return (
                <li key={e.id} className={nuestro ? 'fila-marcada' : undefined}>
                  <div className="aviso-cabeza">
                    <strong>{etiquetaDeAccion(e.action)}</strong>
                    {nuestro && (
                      <span className="pastilla pastilla-info">
                        Personal de Kiap
                      </span>
                    )}
                  </div>

                  <p className="tenue">
                    {e.actor_name ?? 'Sin identificar'}
                    {e.entity_type ? ` · ${e.entity_type}` : ''}
                    {e.ip ? ` · desde ${e.ip}` : ''}
                    {' · '}
                    {fechaYHora(e.occurred_at)}
                  </p>

                  {/*
                    El `metadata` crudo, en clave y valor. No se interpreta porque el
                    servidor mete lo que quiere en cada acción, y un intérprete que no
                    conoce una clave la escondería — en un registro de auditoría,
                    esconder es justo lo que no se puede hacer.
                  */}
                  {datos.length > 0 && (
                    <p className="tenue">
                      {datos
                        .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
                        .join(' · ')}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>

          {/*
            Decir que la lista está cortada. Un registro que se corta en silencio
            hace creer que no hay nada más, que es la conclusión equivocada.
          */}
          {entradas.length === TOPE && (
            <p className="tenue">
              Se enseñan las {TOPE} más recientes. Si buscas algo de antes,
              acota con las fechas.
            </p>
          )}
        </>
      )}
    </section>
  );
}
